import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, RefreshCw } from "lucide-react";
import { CalendarDays as FilterDateIcon, UserCircle2 as FilterOwnerIcon } from "lucide-react";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { useAuth } from "@/context/AuthContext";
import { InlineCell } from "@/components/inline/InlineCell";
import { Button } from "@/components/ui/button";
import { ListPager } from "@/components/ui/list-pager";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FilterBar } from "@/components/filters/FilterBar";
import { ListEmpty, PageHeader, Person, ViewsPanel } from "@/components/patterns";
import { OwnerCell } from "@/components/inline/OwnerCell";
import { BulkEditDialog, type BulkField } from "@/components/inline/BulkEditDialog";
import { useRowSelection } from "@/lib/useRowSelection";
import { BulkBar, BulkButton } from "@/components/patterns";

import { usePersistentFilters } from "@/lib/usePersistentFilters";
import { useFieldFilters, type FilterFieldDef } from "@/lib/useFieldFilters";
import { useSort } from "@/lib/useSort";
import { dateRangeParams, ownerOptionsFromFacets, toQuery, type OwnerFacet } from "@/lib/filters";
import { dateLocale } from "@/lib/dateTime";
import { formatINR } from "@/lib/utils";
import { OrderStatusBadge, ORDER_STATUS_LABELS } from "./OrderStatusBadge";
import type { OrderRecord, OrderStatus } from "./orderTypes";

const ORDER_FILTER_DEFAULTS = { owner: "all", date: "any" };
const STATUSES = Object.keys(ORDER_STATUS_LABELS) as OrderStatus[];

const formatDate = (d?: string | null) =>
  d ? new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short", year: "numeric" }) : "-";

const ORDER_BULK_FIELDS: BulkField[] = [
  { field: "customer_po_number", label: "Customer PO number", kind: "text", nullable: true },
  { field: "delivery_date", label: "Delivery date", kind: "date", nullable: true },
];

export function OrdersPage() {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const canEditInline = hasPermission("order.update");
  const filters = usePersistentFilters("orders", ORDER_FILTER_DEFAULTS);

  // "+ Add filter" on any field the API offers
  const fieldFilters = useFieldFilters("orders");
  const sorting = useSort("orders");
  const [fieldDefs, setFieldDefs] = useState<FilterFieldDef[] | null>(null);
  useEffect(() => {
    api.get("/api/SalesOrder/FilterFields").then((res) => setFieldDefs(unwrapArray<FilterFieldDef>(res))).catch(() => setFieldDefs([]));
  }, []);
  const [tab, setTab] = useState<string>("ALL");
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState<{ total: number; owners: OwnerFacet[]; statuses: { value: string; count: number }[] }>({ total: 0, owners: [], statuses: [] });

  const list = usePagedList<OrderRecord>({
    deps: [tab, search, filters.values, fieldFilters.values, fieldDefs, sorting.sort],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => {
      const f = filters.values;
      const query = toQuery({
        status: STATUSES.includes(tab as OrderStatus) ? tab : undefined,
        search: search.trim(),
        owner: tab === "MINE" ? "me" : f.owner,
        ...dateRangeParams(f.date, "date"),
        f: fieldFilters.param(fieldDefs), sort: sorting.sort,
        page,
        page_size: 30,
      });
      const [res, opt] = await Promise.all([api.get(`/api/SalesOrder/List${query}`), api.get("/api/SalesOrder/FilterOptions").catch(() => null)]);
      if (opt) setOptions(unwrapObject(opt));
      return unwrapPage<OrderRecord>(res);
    },
  });
  const [mineCount, setMineCount] = useState<number | undefined>();
  useEffect(() => {
    api
      .get("/api/SalesOrder/List?owner=me&page_size=1")
      .then((r) => setMineCount(unwrapPage(r).total))
      .catch(() => setMineCount(undefined));
  }, [list.total]);

  const count = (s: string) => options.statuses.find((x) => x.value === s)?.count;
  const orders = list.rows;
  const sel = useRowSelection(orders.map((o) => o.id));
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const title = tab === "ALL" ? "All orders" : tab === "MINE" ? "My orders" : ORDER_STATUS_LABELS[tab as OrderStatus] || "Orders";

  return (
    <div className="space-y-4">
      <ViewsPanel
        title="Orders"
        subtitle="Sales orders"
        active={tab}
        onSelect={setTab}
        groups={[
          { items: [{ key: "ALL", label: "All orders", count: options.total }, { key: "MINE", label: "My orders", count: mineCount }] },
          { heading: "Status", items: STATUSES.map((s) => ({ key: s, label: ORDER_STATUS_LABELS[s], count: count(s) ?? 0 })) },
        ]}
      />

      <PageHeader
        title={title}
        summary={list.total}
        actions={
          <Button variant="outline" size="sm" className="hidden md:inline-flex" onClick={list.reload} disabled={list.loading}>
            <RefreshCw className={`size-3.5 mr-1.5 ${list.loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: "Search by order, PO, customer or deal..." }}
        fields={[
          { name: "owner", label: "Owner", icon: FilterOwnerIcon, type: "select", options: ownerOptionsFromFacets(options.owners, options.total, user?.Id), defaultValue: "all" },
          { name: "date", label: "Order date", icon: FilterDateIcon, type: "date", dates: "past", defaultValue: "any" },
        ]}
        values={filters.values}
        onChange={(name, value) => filters.set(name as keyof typeof ORDER_FILTER_DEFAULTS, value)}
        onReset={filters.reset}
        quickKey="orders"
        fieldFilters={{ fields: fieldDefs || [], value: fieldFilters.values, onChange: fieldFilters.setAll, suggestUrl: "/api/SalesOrder/FilterValues" }}
        sort={{ fields: fieldDefs || [], value: sorting.sort, onChange: sorting.setSort }}
        shown={list.total}
        total={list.total}
        noun="orders"
      />

      <BulkBar count={sel.count} onClear={sel.clear}>
        {canEditInline && <BulkButton onClick={() => setBulkEditOpen(true)}>Edit a field</BulkButton>}
      </BulkBar>
      <BulkEditDialog open={bulkEditOpen} onClose={() => setBulkEditOpen(false)} list="SalesOrder" ids={sel.ids} fields={ORDER_BULK_FIELDS} onDone={() => { sel.clear(); list.reload(); }} />

      {list.loading && orders.length === 0 ? (
        <TableSkeleton rows={6} columns={7} />
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          <Table className="min-w-[960px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <input type="checkbox" aria-label="Select all" checked={sel.allOn} onChange={sel.toggleAll} className="rounded border-border size-4 accent-primary cursor-pointer" />
                </TableHead>
                <TableHead>Order</TableHead>
                <TableHead>Customer / Deal</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Order date</TableHead>
                <TableHead>Delivery</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((o) => (
                <TableRow key={o.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/orders/${o.id}`)}>
                  <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" aria-label="Select row" checked={sel.selected.has(o.id)} onChange={() => sel.toggle(o.id)} className="rounded border-border size-4 accent-primary cursor-pointer" />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <div className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <ClipboardList className="size-4" />
                      </div>
                      <div>
                        <div className="font-semibold text-sm font-mono">{o.order_number}</div>
                        <div className="text-[11px] text-muted-foreground">{o.customer_po_number ? `PO ${o.customer_po_number}` : o.quotation?.display_number || "No PO yet"}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm font-medium">{o.account?.name || "-"}</div>
                    <div className="text-xs text-muted-foreground">{o.deal?.name}</div>
                  </TableCell>
                  <TableCell>
                    <span onClick={(e) => e.stopPropagation()}><OwnerCell list="SalesOrder" id={o.id} ownerId={o.owner_id} owner={o.owner} enabled={canEditInline && !["DELIVERED", "CANCELLED"].includes(o.order_status)} onSaved={(row) => list.patchRow(o.id, row)} onConflict={list.reload} /></span>
                  </TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{formatDate(o.order_date)}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap"><span onClick={(e) => e.stopPropagation()}>
                      <InlineCell list="SalesOrder" id={o.id} field="delivery_date" kind="date" value={o.delivery_date} display={formatDate(o.delivery_date)} enabled={canEditInline && !["DELIVERED", "CANCELLED"].includes(o.order_status)} onSaved={(row) => list.patchRow(o.id, row)} onConflict={list.reload} />
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-semibold whitespace-nowrap">{formatINR(o.grand_total)}</TableCell>
                  <TableCell>
                    <OrderStatusBadge status={o.order_status} />
                  </TableCell>
                </TableRow>
              ))}
              {orders.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="p-0">
                    <ListEmpty
                      noun="orders"
                      error={list.error}
                      onRetry={list.reload}
                      icon={ClipboardList}
                      filtered={!!search.trim() || tab !== "ALL" || filters.activeCount > 0 || fieldFilters.activeCount > 0}
                      onClear={() => {
                        setSearch("");
                        setTab("ALL");
                        filters.reset();
                        fieldFilters.reset();
                      }}
                      firstUse="Orders are made from an accepted quote, or from a Won deal. Open the deal and choose Create order."
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={list.loading} />
        </div>
      )}
    </div>
  );
}
